import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import type { JobApplication } from "../data/api/JobApplicationClient";

interface ApplicationCardProps {
  app: JobApplication;
  columnStage: string;
  isProcessing: boolean;
  stageIndex: number;
  totalStages: number;
  isSelected: boolean;
  onToggleSelect: (app: JobApplication) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, app: JobApplication) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onOpenResume: (url: string) => void;
  onReject: (app: JobApplication) => void;
  onMoveToStage: (app: JobApplication, direction: "PREV" | "NEXT") => void;
  onClickMatchScore: (app: JobApplication) => void;
}

const getMatchBadge = (score: number) => {
  if (score >= 75) return { bg: "rgba(5,150,105,0.1)", color: "#059669", bar: "#10b981", label: "Strong Match" };
  if (score >= 50) return { bg: "rgba(245,158,11,0.1)", color: "#b45309", bar: "#f59e0b", label: "Good Match" };
  return { bg: "rgba(100,116,139,0.1)", color: "#64748b", bar: "#94a3b8", label: "Weak Match" };
};

const initials = (name: string) => name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?";

const getAvatarColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const palette = ["#059669", "#0891b2", "#7c3aed", "#db2777", "#d97706", "#0284c7", "#dc2626", "#16a34a"];
  return palette[Math.abs(hash) % palette.length];
};

const formatAppliedDate = (dateStr?: string | null) => {
  if (!dateStr) return "Unknown date";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    "  " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const ApplicationCard: React.FC<ApplicationCardProps> = ({
  app,
  isProcessing,
  stageIndex,
  totalStages,
  isSelected,
  onToggleSelect,
  onDragStart,
  onDragEnd,
  onOpenResume,
  onReject,
  onMoveToStage,
  onClickMatchScore,
}) => {
  const [hovered, setHovered] = useState(false);
  const name = app.candidate_name || "Unnamed Candidate";
  const showScore = app.match_status === "COMPLETE" && app.match_score != null;
  const isRejected = app.stage === "Rejected";
  const badge = showScore && app.match_score != null ? getMatchBadge(app.match_score) : null;
  const avatarBg = getAvatarColor(name);

  return (
    <div
      className={`kan-card ${isProcessing ? "kan-card--processing" : ""}`}
      draggable={!isProcessing}
      onDragStart={(e) => onDragStart(e, app)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderColor: isSelected
          ? "rgba(5,150,105,0.5)"
          : hovered
          ? "rgba(5,150,105,0.25)"
          : "rgba(255,255,255,0.7)",
        boxShadow: isSelected
          ? "0 0 0 2px rgba(5,150,105,0.2), 0 4px 12px rgba(5,150,105,0.1)"
          : undefined,
        opacity: isProcessing ? 0.6 : 1,
      }}
    >
      {/* Top row: checkbox + avatar + name/date */}
      <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
        <input
          type="checkbox"
          className="kan-checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(app)}
          onClick={(e) => e.stopPropagation()}
          style={{ marginTop: "6px", flexShrink: 0 }}
        />

        {/* Avatar */}
        <div style={{
          width: "38px", height: "38px", borderRadius: "50%", flexShrink: 0,
          background: avatarBg, display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: "14px",
          boxShadow: `0 2px 8px ${avatarBg}55`,
        }}>
          {initials(name)}
        </div>

        {/* Name + Date */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontSize: "0.92rem", fontWeight: 700, color: "#0f172a", margin: "0 0 3px 0",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {name}
          </p>
          <p style={{ fontSize: "0.72rem", color: "#94a3b8", margin: 0, fontWeight: 500 }}>
            Applied: {formatAppliedDate(app.created_at)}
          </p>
          {(app.rejection_reason_type || app.rejection_reason_text) && (
            <div style={{
              display: "inline-block", marginTop: "5px",
              padding: "2px 6px", borderRadius: "4px",
              background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca",
              fontSize: "0.7rem", fontWeight: 600,
              maxWidth: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
            }} title={app.rejection_reason_text || app.rejection_reason_type || ""}>
              {app.rejection_reason_text || app.rejection_reason_type}
            </div>
          )}
        </div>
      </div>

      {/* Match Score Badge */}
      <div style={{ paddingLeft: "48px", marginTop: "10px" }}>
        {showScore && badge ? (
          <div
            onClick={() => onClickMatchScore(app)}
            style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              cursor: "pointer", padding: "5px 10px", borderRadius: "8px",
              background: badge.bg, border: `1px solid ${badge.color}30`,
              transition: "transform 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            {/* Mini bar */}
            <div style={{ width: "32px", height: "4px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
              <div style={{ width: `${app.match_score}%`, height: "100%", background: badge.bar, borderRadius: "2px" }} />
            </div>
            <span style={{ fontSize: "12px", fontWeight: 800, color: badge.color }}>
              {Math.round(app.match_score!)}%
            </span>
            <span style={{ fontSize: "11px", fontWeight: 600, color: badge.color, opacity: 0.8 }}>
              {badge.label}
            </span>
          </div>
        ) : app.match_status === "PROCESSING" ? (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            padding: "4px 10px", borderRadius: "12px",
            background: "#eff6ff", border: "1px solid #bfdbfe",
            color: "#4f46e5", fontSize: "11px", fontWeight: 700
          }}>
            <Loader2 size={12} className="kan-spin" /> Scoring...
          </div>
        ) : (
          <span style={{ fontSize: "12px", color: "#cbd5e1", fontStyle: "italic" }}>No score</span>
        )}
      </div>

      {/* Expanding Hover Action Strip */}
      <div style={{
        overflow: "hidden",
        maxHeight: hovered ? "60px" : "0",
        opacity: hovered ? 1 : 0,
        transition: "max-height 0.28s ease, opacity 0.22s ease, margin-top 0.28s ease",
        marginTop: hovered ? "12px" : "0",
      }}>
        <div style={{
          borderTop: "1px solid rgba(5,150,105,0.12)",
          paddingTop: "10px",
          display: "flex",
          gap: "7px",
        }}>
          {/* Match Pop-up */}
          <button
            onClick={(e) => { e.stopPropagation(); onClickMatchScore(app); }}
            style={{
              flex: 1, padding: "6px 0", fontSize: "0.78rem", fontWeight: 600,
              borderRadius: "8px", cursor: "pointer",
              background: "rgba(255,255,255,0.6)", border: "1px solid rgba(100,116,139,0.2)",
              backdropFilter: "blur(6px)", color: "#334155",
              transition: "background 0.15s, border-color 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget.style.background = "rgba(255,255,255,0.9)"); }}
            onMouseLeave={(e) => { (e.currentTarget.style.background = "rgba(255,255,255,0.6)"); }}
          >
            Match
          </button>


          {/* Next Stage */}
          <button
            disabled={isProcessing || stageIndex >= totalStages - 1 || isRejected}
            onClick={(e) => { e.stopPropagation(); onMoveToStage(app, "NEXT"); }}
            style={{
              flex: 1, padding: "6px 0", fontSize: "0.78rem", fontWeight: 600,
              borderRadius: "8px",
              cursor: (isProcessing || stageIndex >= totalStages - 1 || isRejected) ? "not-allowed" : "pointer",
              background: "rgba(5,150,105,0.08)", border: "1px solid rgba(5,150,105,0.25)",
              backdropFilter: "blur(6px)", color: "#059669",
              transition: "background 0.15s",
              opacity: (isProcessing || stageIndex >= totalStages - 1 || isRejected) ? 0.4 : 1,
            }}
            onMouseEnter={(e) => { (e.currentTarget.style.background = "rgba(5,150,105,0.15)"); }}
            onMouseLeave={(e) => { (e.currentTarget.style.background = "rgba(5,150,105,0.08)"); }}
          >
            Advance
          </button>

          {/* Reject */}
          <button
            disabled={isProcessing || isRejected}
            onClick={(e) => { e.stopPropagation(); onReject(app); }}
            style={{
              flex: 1, padding: "6px 0", fontSize: "0.78rem", fontWeight: 600,
              borderRadius: "8px",
              cursor: (isProcessing || isRejected) ? "not-allowed" : "pointer",
              background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.22)",
              backdropFilter: "blur(6px)", color: "#dc2626",
              transition: "background 0.15s",
              opacity: (isProcessing || isRejected) ? 0.4 : 1,
            }}
            onMouseEnter={(e) => { (e.currentTarget.style.background = "rgba(239,68,68,0.14)"); }}
            onMouseLeave={(e) => { (e.currentTarget.style.background = "rgba(239,68,68,0.07)"); }}
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationCard;
